import { supabase, Resume, ResumeEducation, ResumeWork, TalentTag, TalentStatusHistory, TalentCommunication } from './supabase';

export interface TalentDetail extends Resume {
  education?: ResumeEducation[];
  work?: ResumeWork[];
  tags?: TalentTag[];
  statusHistory?: TalentStatusHistory[];
  communications?: TalentCommunication[];
}

export interface CreateTalentData {
  name: string;
  phone: string;
  email?: string;
  gender?: string;
  birth_date?: string;
  id_number?: string;
  wechat?: string;
  linkedin_url?: string;
  position?: string;
  location?: string;
  highest_education?: string;
  graduation_school?: string;
  major?: string;
  graduation_year?: number;
  current_company?: string;
  current_position?: string;
  current_salary?: string;
  expected_salary?: string;
  years_of_experience?: number;
  job_search_status?: string;
  available_date?: string;
  preferred_industry?: string[];
  preferred_position?: string;
  preferred_company_type?: string;
  work_mode_preference?: string;
  overtime_acceptance?: string;
  travel_acceptance?: string;
  language_skills?: Record<string, string>;
  certifications?: string[];
  skills?: string[];
  self_evaluation?: string;
  source?: string;
  referrer?: string;
  notes?: string;
  status?: string;
}

// 获取所有人才列表
export async function getTalents(filters?: {
  status?: string;
  location?: string;
  skills?: string[];
  search?: string;
}) {
  let query = supabase
    .from('resumes')
    .select('*')
    .order('updated_at', { ascending: false });

  if (filters?.status) {
    query = query.eq('current_status', filters.status);
  }

  if (filters?.location) {
    query = query.eq('location', filters.location);
  }

  if (filters?.search) {
    query = query.or(`name.ilike.%${filters.search}%,position.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data as Resume[];
}

// 获取人才详情
export async function getTalentDetail(id: string): Promise<TalentDetail | null> {
  const { data: resume, error: resumeError } = await supabase
    .from('resumes')
    .select('*')
    .eq('id', id)
    .single();

  if (resumeError) throw resumeError;
  if (!resume) return null;

  // 获取教育经历
  const { data: education } = await supabase
    .from('resume_education')
    .select('*')
    .eq('resume_id', id)
    .order('start_date', { ascending: false });

  // 获取工作经历
  const { data: work } = await supabase
    .from('resume_work')
    .select('*')
    .eq('resume_id', id)
    .order('start_date', { ascending: false });

  // 获取标签
  const { data: tags } = await supabase
    .from('talent_tags')
    .select('*')
    .eq('resume_id', id);

  // 获取状态历史
  const { data: statusHistory } = await supabase
    .from('talent_status_history')
    .select('*')
    .eq('resume_id', id)
    .order('created_at', { ascending: false });

  // 获取沟通记录
  const { data: communications } = await supabase
    .from('talent_communications')
    .select('*')
    .eq('resume_id', id)
    .order('created_at', { ascending: false });

  return {
    ...resume,
    education: education || [],
    work: work || [],
    tags: tags || [],
    statusHistory: statusHistory || [],
    communications: communications || []
  };
}

// 创建人才
export async function createTalent(talent: Partial<Resume>) {
  const { data, error } = await supabase
    .from('resumes')
    .insert([{
      ...talent,
      current_status: talent.current_status || '待沟通',
      raw_response: talent.raw_response || {}, // 添加必填字段 raw_response
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }])
    .select()
    .single();

  if (error) throw error;
  return data as Resume;
}

// 更新人才信息
export async function updateTalent(id: string, talent: Partial<Resume>) {
  const { data, error } = await supabase
    .from('resumes')
    .update({
      ...talent,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Resume;
}

// 删除人才
export async function deleteTalent(id: string) {
  const { error } = await supabase
    .from('resumes')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// 添加教育经历
export async function addEducation(education: Partial<ResumeEducation>) {
  const { data, error } = await supabase
    .from('resume_education')
    .insert([education])
    .select()
    .single();

  if (error) throw error;
  return data as ResumeEducation;
}

// 添加工作经历
export async function addWork(work: Partial<ResumeWork>) {
  const { data, error } = await supabase
    .from('resume_work')
    .insert([work])
    .select()
    .single();

  if (error) throw error;
  return data as ResumeWork;
}

// 添加标签
export async function addTag(tag: Partial<TalentTag>) {
  const { data, error } = await supabase
    .from('talent_tags')
    .insert([{
      ...tag,
      created_at: new Date().toISOString()
    }])
    .select()
    .single();

  if (error) throw error;
  return data as TalentTag;
}

// 删除标签
export async function deleteTag(id: string) {
  const { error } = await supabase
    .from('talent_tags')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// 更新状态
export async function updateTalentStatus(resumeId: string, status: string, notes?: string, projectId?: string) {
  // 更新简历状态
  await supabase
    .from('resumes')
    .update({ 
      current_status: status,
      updated_at: new Date().toISOString()
    })
    .eq('id', resumeId);

  // 添加状态历史记录
  const { data, error } = await supabase
    .from('talent_status_history')
    .insert([{
      resume_id: resumeId,
      status,
      notes,
      project_id: projectId,
      created_at: new Date().toISOString()
    }])
    .select()
    .single();

  if (error) throw error;
  return data as TalentStatusHistory;
}

// 添加沟通记录
export async function addCommunication(communication: Partial<TalentCommunication>) {
  const { data, error } = await supabase
    .from('talent_communications')
    .insert([{
      ...communication,
      created_at: new Date().toISOString()
    }])
    .select()
    .single();

  if (error) throw error;
  return data as TalentCommunication;
}

// 批量导入人才
export async function batchImportTalents(talents: Partial<Resume>[]) {
  const { data, error } = await supabase
    .from('resumes')
    .insert(talents.map(t => ({
      ...t,
      current_status: t.current_status || '待沟通',
      raw_response: t.raw_response || {}, // 添加必填字段 raw_response
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })))
    .select();

  if (error) throw error;
  return data as Resume[];
}

// 检查重复人才
export async function checkDuplicateTalent(phone?: string, email?: string) {
  if (!phone && !email) return [];

  let query = supabase.from('resumes').select('*');

  if (phone && email) {
    query = query.or(`phone.eq.${phone},email.eq.${email}`);
  } else if (phone) {
    query = query.eq('phone', phone);
  } else if (email) {
    query = query.eq('email', email);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data as Resume[];
}

// 创建人才（使用 CreateTalentData 接口）
export async function createTalentWithDetails(data: CreateTalentData): Promise<Resume | null> {
  try {
    const { data: newTalent, error } = await supabase
      .from('resumes')
      .insert([{
        ...data,
        current_status: data.status || 'active',
        raw_response: {}, // 添加必填字段 raw_response
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }])
      .select()
      .single();

    if (error) throw error;
    return newTalent;
  } catch (error) {
    console.error('创建人才失败:', error);
    return null;
  }
}

// 添加教育经历到指定简历
export async function addEducationToResume(resumeId: string, education: {
  school: string;
  degree: string;
  major: string;
  start_date: string;
  end_date: string;
}): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('resume_education')
      .insert([{
        resume_id: resumeId,
        ...education,
      }]);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('添加教育经历失败:', error);
    return false;
  }
}

// 添加工作经历
export async function addWorkExperience(resumeId: string, work: {
  company: string;
  position: string;
  start_date: string;
  end_date: string;
  description: string;
}): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('resume_work')
      .insert([{
        resume_id: resumeId,
        ...work,
      }]);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('添加工作经历失败:', error);
    return false;
  }
}
